import { IDX } from '../constants';
import type { CaptureSequence, Landmark } from '../types';

export type PelvisPinType = 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS';

function sqDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Normalize AP/posterior side-specific pelvis pin type using nearest hip landmark.
 * This keeps left/right assignment deterministic even when users click out of sequence
 * or when point order crosses, which stabilizes downstream red-line behavior.
 */
export function normalizePelvisPinTypeByHipSide(
  view: CaptureSequence,
  type: PelvisPinType,
  point: { x: number; y: number },
  landmarks: Landmark[] | null | undefined
): PelvisPinType {
  if (!landmarks || (view !== 'Anterior' && view !== 'Posterior')) return type;

  const isAsisType = type === 'L_ASIS' || type === 'R_ASIS';
  const isPsisType = type === 'L_PSIS' || type === 'R_PSIS';
  if (!isAsisType && !isPsisType) return type;

  const lHip = landmarks[IDX.L_HIP];
  const rHip = landmarks[IDX.R_HIP];
  if (!lHip || !rHip) return type;

  const dLeft = sqDist(point, lHip);
  const dRight = sqDist(point, rHip);
  const isLeftSide = dLeft <= dRight;

  if (isAsisType) return isLeftSide ? 'L_ASIS' : 'R_ASIS';
  return isLeftSide ? 'L_PSIS' : 'R_PSIS';
}

